/**
 * TopicIntelligencePanel
 * Two-column panel: left = horizontal bar chart of top N topics,
 * right = TopicTrendChart (stacked area).
 * Filter bar: template selector, bot, tenant (with recursive descendants), top N.
 */

import { useState, useMemo } from 'react';
import { Select, Typography, Empty, Spin, Switch, theme } from 'antd';
import { TopicTrendChart } from '../TopicTrendChart';
import { TopicDrilldownModal } from '../TopicDrilldownModal';
import type {
  TopicsOverview,
  TenantNode,
  InsightFilters,
  InsightTemplate,
} from '../../types/insights';
import { useLanguage } from '@/hooks/useLanguage';
import { useChartTheme } from '../../utils/chartTheme';
import {
  renderAgentOption,
  renderTenantOption,
  renderTopNOption,
  renderTopNLabel,
  AgentPlaceholder,
  TenantPlaceholder,
  buildTenantOptions,
  tenantFilterOption,
  AGENT_SELECT_STYLE,
  TENANT_SELECT_STYLE,
  AGENT_DROPDOWN_STYLE,
  TENANT_DROPDOWN_STYLE,
} from '../../utils/selectRenderers';

const { Text } = Typography;

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

// ─── Recursive tenant expansion ───────────────────────────────────────────────

function expandTenantIds(selectedIds: string[], tree: TenantNode[]): string[] {
  const result = new Set(selectedIds);
  const queue = [...selectedIds];
  while (queue.length) {
    const id = queue.shift()!;
    tree.filter(t => t.parent_tenant_id === id).forEach(child => {
      if (!result.has(child.tenant_id)) {
        result.add(child.tenant_id);
        queue.push(child.tenant_id);
      }
    });
  }
  return Array.from(result);
}

// ─── Word cloud ──────────────────────────────────────────────────────────────

function WordCloud({ distribution }: { distribution: Record<string, number> }) {
  const entries = useMemo(() => {
    return Object.entries(distribution).sort((a, b) => b[1] - a[1]);
  }, [distribution]);

  if (entries.length === 0) return null;

  const maxCount = entries[0][1];
  const minCount = entries[entries.length - 1][1];
  const range = Math.max(maxCount - minCount, 1);

  // Map count to font size (14px – 42px) and opacity (0.5 – 1.0)
  const getStyle = (count: number, idx: number): React.CSSProperties => {
    const ratio = (count - minCount) / range;
    const fontSize = 14 + ratio * 28;
    const opacity = 0.5 + ratio * 0.5;
    const color = PALETTE[idx % PALETTE.length];
    return { fontSize, opacity, color, fontWeight: ratio > 0.6 ? 700 : 500 };
  };

  // Shuffle for visual variety (deterministic based on entry count)
  const shuffled = useMemo(() => {
    const arr = [...entries];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = (i * 7 + arr.length) % (i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [entries]);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px 16px',
        padding: '12px 8px',
        minHeight: 80,
      }}
    >
      {shuffled.map(([label, count]) => {
        const originalIdx = entries.findIndex(e => e[0] === label);
        return (
          <span
            key={label}
            style={{
              ...getStyle(count, originalIdx),
              cursor: 'default',
              transition: 'transform 0.15s ease',
              lineHeight: 1.3,
            }}
            title={`${label}: ${count}`}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TopicIntelligencePanelProps {
  topicsOverview: TopicsOverview | null;
  loading: boolean;
  chatbots: { chatbot_id: string; chatbot_name: string; tenant_id: string }[];
  tenantTree: TenantNode[];
  filters: InsightFilters;
  templates: InsightTemplate[];
  onFilterChange: (chatbotIds: string[], tenantIds: string[], topN: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TopicIntelligencePanel({
  topicsOverview,
  loading,
  chatbots,
  tenantTree,
  filters,
  onFilterChange,
}: TopicIntelligencePanelProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const ct = useChartTheme();
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [topN, setTopN] = useState<number>(10);
  const [includeDescendants, setIncludeDescendants] = useState(false);

  // Drilldown modal
  const [drilldownTag, setDrilldownTag] = useState<string | null>(null);


  // Compute effective tenant IDs (with optional recursive expansion)
  const effectiveTenantIds = useMemo(() => {
    return includeDescendants && selectedTenants.length > 0 ? expandTenantIds(selectedTenants, tenantTree) : selectedTenants;
  }, [selectedTenants, includeDescendants, tenantTree]);


  const handleBotChange = (vals: string[]) => {
    setSelectedBots(vals);
    onFilterChange(vals, effectiveTenantIds, topN);
  };

  const handleTenantChange = (vals: string[]) => {
    setSelectedTenants(vals);
    const expanded = includeDescendants && vals.length > 0 ? expandTenantIds(vals, tenantTree) : vals;
    onFilterChange(selectedBots, expanded, topN);
  };

  const handleTopNChange = (val: number) => {
    setTopN(val);
    onFilterChange(selectedBots, effectiveTenantIds, val);
  };

  const handleDescendantsToggle = (checked: boolean) => {
    setIncludeDescendants(checked);
    const expanded = checked && selectedTenants.length > 0 ? expandTenantIds(selectedTenants, tenantTree) : selectedTenants;
    onFilterChange(selectedBots, expanded, topN);
  };


  // Group by name so shared agents appear once
  const botOptions = useMemo(() => {
    const byName = new Map<string, string[]>();
    chatbots.forEach(c => {
      const ids = byName.get(c.chatbot_name) || [];
      if (!ids.includes(c.chatbot_id)) ids.push(c.chatbot_id);
      byName.set(c.chatbot_name, ids);
    });
    return Array.from(byName.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, ids]) => ({ value: ids.join(','), label: name }));
  }, [chatbots]);

  const displayDistribution = topicsOverview?.distribution ?? null;
  const displayTimeseries = topicsOverview?.timeseries ?? null;
  const hasData = displayDistribution && Object.keys(displayDistribution).length > 0;
  const hasNoTemplates = topicsOverview !== null && topicsOverview.template_count === 0;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Agent selector */}
        <Select
          mode="multiple"
          size="small"
          value={selectedBots}
          onChange={handleBotChange}
          options={botOptions}
          style={AGENT_SELECT_STYLE}
          placeholder={<AgentPlaceholder />}
          popupMatchSelectWidth={false}
          dropdownStyle={AGENT_DROPDOWN_STYLE}
          showSearch
          optionFilterProp="label"
          maxTagCount={2}
          allowClear
          optionRender={renderAgentOption}
        />

        {/* Tenant selector */}
        <Select
          mode="multiple"
          size="small"
          value={selectedTenants}
          onChange={handleTenantChange}
          options={buildTenantOptions(tenantTree)}
          style={TENANT_SELECT_STYLE}
          placeholder={<TenantPlaceholder />}
          popupMatchSelectWidth={false}
          dropdownStyle={TENANT_DROPDOWN_STYLE}
          showSearch
          maxTagCount={2}
          allowClear
          filterOption={tenantFilterOption}
          optionRender={(option) => renderTenantOption(option, tenantTree, ct.mutedText)}
        />

        {/* Include descendants toggle */}
        {selectedTenants.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Switch
              size="small"
              checked={includeDescendants}
              onChange={handleDescendantsToggle}
            />
            <Text style={{ fontSize: 12, color: token.colorTextSecondary, whiteSpace: 'nowrap' }}>
              + descendants
            </Text>
          </div>
        )}

        {/* Top N selector */}
        <Select
          size="small"
          value={topN}
          onChange={handleTopNChange}
          options={[
            { value: 5,  label: t('insights.top5') },
            { value: 10, label: t('insights.top10') },
            { value: 15, label: t('insights.top15') },
            { value: 20, label: t('insights.top20') },
            { value: 0,  label: t('insights.pulse.allTopics') },
          ]}
          style={{ minWidth: 140, maxWidth: 200 }}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 200 }}
          optionRender={renderTopNOption}
          labelRender={renderTopNLabel}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : hasNoTemplates ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                {t('insights.pulse.noAutoTopicTemplates')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('insights.pulse.noAutoTopicTemplatesHint', { defaultValue: 'Activate the "Conversation Topics" template or run it manually from the Management tab.' })}
              </Text>
            </div>
          }
          style={{ padding: '40px 0' }}
        />
      ) : !hasData ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary" style={{ fontSize: 13 }}>{t('insights.pulse.noTopicData')}</Text>}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Trend chart (full width) */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: '16px',
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: 700, color: ct.mutedText, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
              {t('insights.trendOverTime')}
            </Text>
            {displayTimeseries ? (
              <TopicTrendChart
                data={displayTimeseries}
                loading={false}
                distribution={displayDistribution ?? undefined}
                onTopicClick={setDrilldownTag}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('insights.pulse.noTrendData')} />
            )}
          </div>

          {/* Word cloud */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: '16px',
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: 700, color: ct.mutedText, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 12 }}>
              {t('insights.wordCloud')}
            </Text>
            <WordCloud distribution={displayDistribution!} />
          </div>
        </div>
      )}

      {/* Topic drilldown modal */}
      <TopicDrilldownModal
        open={!!drilldownTag}
        tag={drilldownTag}
        onClose={() => setDrilldownTag(null)}
        filters={{
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          tenantIds: effectiveTenantIds.length > 0 ? effectiveTenantIds : undefined,
          chatbotIds: selectedBots.length > 0 ? selectedBots.flatMap(id => id.split(',')) : undefined,
        }}
      />
    </div>
  );
}
